import {LitElement, html} from 'lit';
import {property, customElement} from 'lit/decorators.js';
import {Service} from '../../service/service.js';
import {ElementUtil} from '../../utils/elementUtil.js';
import {ExportUtil} from '../../utils/exportUtil.js';
import {TimeUtil} from '../../utils/timeUtil.js';
import {GridColumnLayoutUtil} from '../../utils/gridColumnLayoutUtil.js';
import {BaseComponent} from '../basecomponent.js';

/* global customElements */
class ExportToolbar extends BaseComponent {
	
	static get properties() {
		return {			
			showCSV: {type: Boolean},
			showDetail: {type: Boolean},
			showColumnsVisiblity: {type: Boolean},
			gridId: {type: String}
		};
	}
	
	constructor() {
		super();
		
		var exportToolbar = this;
		
		exportToolbar.showCSV = true;
		exportToolbar.showDetail = true;
		exportToolbar.showColumnsVisiblity = true;
		exportToolbar.gridId = exportToolbar.gridId || 'primary';
		
		setTimeout(function(){
			exportToolbar.init();
		});
	}
	
	render() {	
		return html`
			<style>
				.toolbar {
					width: 100%;
					display: flex;
					flex-wrap: wrap;
					align-items: center;
					gap: 8px;
				}
		
				.right {
					margin-left: auto;
					margin-right: 10px;
					display: flex;
					align-items: center;
					gap: 8px;
				}
		
				.status-text {
					margin-right: 20px;
					color: grey;
					position: static;
				}
		
				.hide {
					display: none
				}

				@media only screen and (max-width: 1024px) {
					.right {
						margin-left: 0;
						width: 100%;
					}
				}
		
			</style>
			<div class="toolbar">
				<vaadin-button itemid='csv' style="margin-left: 5px;" class='${this.showCSV ? '' : 'hide'}' title="Export the current page as shown (visible columns)" @click='${this.clickCSV}'>
					<vaadin-icon icon="vaadin:download-alt"></vaadin-icon> CSV					
				</vaadin-button>
				<vaadin-button itemid='detail' class='${this.showDetail ? '' : 'hide'}' title="Export all filtered results from the server" @click='${this.clickDetail}'>
					<vaadin-icon icon="vaadin:download-alt"></vaadin-icon> Detail
				</vaadin-button>
				<span>
					<slot name="extra"></slot>
				</span>				
				<span class="right">
					<span class="status-text">
						<slot name="status"></slot>
					</span>
					<slot name="column-visiblity"></slot>
					<slot name="grid-options"></slot>
				</span>
			</div>
		`;
	}
	
	init() {
		var exportToolbar = this;
		
		exportToolbar.gridOptionsMenu = ElementUtil.create({
			tag: 'vaadin-context-menu',
			'open-on': 'click',
			slot: 'grid-options',
			children: [
				{
					tag: 'vaadin-button',
					theme: 'tertiary icon',
					title: 'Grid options',
					children: [
						{
							tag: 'vaadin-icon',
							icon: 'vaadin:cog'
						}
					]
				}
			],
			listeners: {
				'item-selected': function(e) {
					var value = e.detail.value;
					if (!value || value.key !== 'wrapText' || !exportToolbar.grid) {
						return;
					}
					var enabled = !GridColumnLayoutUtil.isWrapTextEnabled(exportToolbar.grid);
					GridColumnLayoutUtil.applyWrapText(exportToolbar.grid, enabled);
					exportToolbar.updateGridOptionsMenu();
					GridColumnLayoutUtil.notifyWrapTextChanged(exportToolbar.grid);
				}
			}
		});
		exportToolbar.append(exportToolbar.gridOptionsMenu);
		exportToolbar.updateGridOptionsMenu();

		exportToolbar.columnMenu = ElementUtil.create({
			tag: 'vaadin-context-menu',
			'open-on': 'click',
			slot: 'column-visiblity',
			children: [
				{
					tag: 'vaadin-button',
					theme: 'tertiary',
					'class': (exportToolbar.showColumnsVisiblity ? '' : 'hide'),
					text: 'Show/Hide Columns'
				}
			],
			listeners: {
				'item-selected': function(e) {
					var value = e.detail.value;
					value.column.hidden = !value.column.hidden;
					// Mutate the same item object so Vaadin's keepOpen handler
					// can find it in items and refresh the checkmark.
					value.checked = !value.column.hidden;
					GridColumnLayoutUtil.notifyColumnVisibilityChanged(exportToolbar.grid);
				}
			}
		});			
		exportToolbar.append(exportToolbar.columnMenu);
		
		var readyEvent = new CustomEvent('ready', { detail: '' });
		exportToolbar.dispatchEvent(readyEvent);		
	}
	
	updateGrid(grid) {
		var exportToolbar = this;
		exportToolbar.grid = grid;	
		exportToolbar.updateColumnVisiblity();
		exportToolbar.updateGridOptionsMenu();
		var gridId = GridColumnLayoutUtil.resolveGridId(exportToolbar);
		GridColumnLayoutUtil.bindPrimaryGrid(grid, {
			gridId: gridId
		});
		if (!grid.__dlColumnLayoutMenuListener) {
			grid.__dlColumnLayoutMenuListener = function() {
				exportToolbar.updateColumnVisiblity();
				exportToolbar.updateGridOptionsMenu();
			};
			grid.addEventListener('dl-column-layout-applied', grid.__dlColumnLayoutMenuListener);
		}
	}

	updateGridOptionsMenu() {
		var exportToolbar = this;
		if (!exportToolbar.gridOptionsMenu) {
			return;
		}
		var wrapEnabled = exportToolbar.grid
			? GridColumnLayoutUtil.isWrapTextEnabled(exportToolbar.grid)
			: false;
		exportToolbar.gridOptionsMenu.items = [
			{
				text: 'Text wrapping',
				checked: wrapEnabled,
				key: 'wrapText'
			}
		];
	}
	
	updateColumnVisiblity() {
		var exportToolbar = this;
		
		var contextMenuItems = [];
		Array.from(exportToolbar.grid.childNodes).forEach(function(child) {
			if (child.tagName === 'VAADIN-GRID-SORT-COLUMN') {
				var hidden = child.hidden ? true : false;
				
				var headerLabel = child.getAttribute('header') || child.path || '';
				contextMenuItems.push({
					text: ExportUtil.escapeHtml(headerLabel),
					checked: !hidden,
					keepOpen: true,
					key: child.getAttribute('path'),
					column: child
				});
			}
		});
		
		exportToolbar.columnMenu.items = contextMenuItems;
	}
	
	clickCSV() {
		var exportToolbar = this;
		var grid = exportToolbar.grid;
		if (!grid) {
			return;
		}

		var columns = [];
		if (GridColumnLayoutUtil.getPathColumnsInVisualOrder) {
			columns = GridColumnLayoutUtil.getPathColumnsInVisualOrder(grid).filter(function(column) {
				return !column.hidden;
			});
		} else {
			Array.from(grid.childNodes).forEach(function(child) {
				if (!child || !child.tagName) {
					return;
				}
				var tagName = child.tagName.toUpperCase();
				if (tagName.indexOf('VAADIN-GRID') !== 0 || tagName.indexOf('COLUMN') === -1) {
					return;
				}
				if (tagName === 'VAADIN-GRID-SELECTION-COLUMN' || child.hidden) {
					return;
				}
				var path = child.path || child.getAttribute('path');
				if (path) {
					columns.push(child);
				}
			});
		}

		if (!columns.length) {
			return;
		}

		var resolvePathValue = function(item, path) {
			if (!item || !path) {
				return '';
			}
			var parts = String(path).split('.');
			var current = item;
			for (var index = 0; index < parts.length; index++) {
				if (current === null || current === undefined) {
					return '';
				}
				current = current[parts[index]];
			}
			return current === null || current === undefined ? '' : current;
		};

		var formatCellValue = function(column, item) {
			var path = column.path || column.getAttribute('path');
			var value = resolvePathValue(item, path);

			// Status indicator columns render CSS-only dots with a title; export the title text.
			if (path === 'failsRunsSinceLastSuccess') {
				if (value === 0 || value === '0') {
					value = 'Healthy';
				} else if (value > 0 && value < 5) {
					value = 'Unhealthy';
				} else if (value >= 5) {
					value = 'Failure';
				}
				if (item && item.warningState) {
					value = (value ? value + ' ' : '') + item.warningState;
				}
			}

			if (value === null || value === undefined) {
				return '';
			}
			if (typeof value === 'object') {
				return JSON.stringify(value);
			}
			if (TimeUtil.looksLikeDateTimeValue(value)) {
				return TimeUtil.formatDateStandard(value);
			}
			return String(value);
		};

		var headers = columns.map(function(column) {
			return ExportUtil.csvEscape(column.header || column.getAttribute('header') || column.path || '');
		});

		var lines = [];
		lines.push(headers.join(','));
		(grid.items || []).forEach(function(item) {
			lines.push(columns.map(function(column) {
				return ExportUtil.csvEscape(formatCellValue(column, item));
			}).join(','));
		});

		ExportUtil.savefile({
			data: lines.join('\n') + '\n',
			fileName: ExportUtil.buildPageExportFileName(exportToolbar.grid),
			type: 'text/csv'
		});

		var customEvent = new CustomEvent('click-csv', { detail: '' });
		exportToolbar.dispatchEvent(customEvent);
	}

	clickDetail() {
		var exportToolbar = this;
		var customEvent = new CustomEvent('click-detail', { detail: '' });
		exportToolbar.dispatchEvent(customEvent);
	}	
	
};
customElements.define('dl-export-toolbar', ExportToolbar);

